
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { DesktopWbsAPI } from "../services/api";
import { useSession } from "../context/SessionContext";

type WorkTreeRecord = {
  TreeNodeNo?: number | string;
  treeNodeNo?: number | string;
  WorkTreeName?: string;
  workTreeName?: string;
  ParentNode?: number | string | null;
  parentNode?: number | string | null;
  Description?: string;
  description?: string;
  Sequence?: number | string;
  sequence?: number | string;
  Project?: number | string;
  project?: number | string;
  IsDeleted?: boolean | number | string;
  isDeleted?: boolean | number | string;
  [key: string]: unknown;
};

type WbsNode = {
  id: string;
  name: string;
  description: string;
  sequence: number;
  parentId: string | null;
  children: WbsNode[];
  original: WorkTreeRecord;
};

const getValue = (
  record: WorkTreeRecord,
  upper: string,
  lower: string
): unknown => record[upper] ?? record[lower];

const getId = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const isDeleted = (record: WorkTreeRecord): boolean => {
  const value = getValue(record, "IsDeleted", "isDeleted");

  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    String(value).toLowerCase() === "true"
  );
};

const BaevrWbs: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSessionNo } = useSession();

  const projectName =
    (location.state as { projectName?: string } | null)?.projectName ||
    `Project ${projectId ?? ""}`;

  // Display the username saved during login.
  const userName =
    localStorage.getItem("userName") ||
    localStorage.getItem("username") ||
    localStorage.getItem("email") ||
    "User";

  // Logout and return to the desktop login screen without restoring the old project.
  const handleLogout = () => {
    // Keep this marker in sessionStorage so Login.tsx skips automatic restoration.
    sessionStorage.setItem("baevrDesktopLoggedOut", "true");

    // Clear the saved project and local user data.
    localStorage.removeItem("baevrDesktopUserId");
    localStorage.removeItem("baevrDesktopProjectId");
    localStorage.removeItem("baevrDesktopProjectName");
    localStorage.clear();

    setSessionNo(null);

    navigate("/login?source=itemizerdesktop", {
      replace: true,
    });
  };

  const [records, setRecords] = useState<WorkTreeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addMessage, setAddMessage] = useState("");

  // Load WBS records.
  useEffect(() => {
    let cancelled = false;

    const loadWorkTree = async () => {
      if (!projectId) {
        setError("Project ID is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await DesktopWbsAPI.getByProject(projectId);

        const rows = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.items)
              ? response.items
              : Array.isArray(response?.result)
                ? response.result
                : null;

        if (!rows) {
          throw new Error(
            "The API response is not an array. Check the response in Network tools."
          );
        }

        if (!cancelled) {
          setRecords(rows);
        }
      } catch (err: any) {
        console.error("Failed to load WorkTree:", err);

        if (!cancelled) {
          setError(
            err?.response?.status
              ? `Unable to load WBS (HTTP ${err.response.status}). Check the API response.`
              : err?.message || "Unable to load this project's WBS."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadWorkTree();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Build the WBS hierarchy.
  const roots = useMemo(() => {
    const activeRecords = records.filter(
      (record) => !isDeleted(record)
    );

    const nodes: WbsNode[] = activeRecords.map((record, index) => {
      const rawId = getValue(record, "TreeNodeNo", "treeNodeNo");
      const rawParent = getValue(record, "ParentNode", "parentNode");
      const rawSequence = getValue(record, "Sequence", "sequence");
      const rawName = getValue(record, "WorkTreeName", "workTreeName");
      const rawDescription = getValue(
        record,
        "Description",
        "description"
      );

      return {
        id: getId(rawId) || `row-${index}`,
        name: String(rawName ?? "Unnamed WBS item"),
        description: String(rawDescription ?? ""),
        sequence: Number(rawSequence) || 0,
        parentId: getId(rawParent) || null,
        children: [],
        original: record,
      };
    });

    const byId = new Map<string, WbsNode>();

    nodes.forEach((node) => {
      if (!byId.has(node.id)) {
        byId.set(node.id, node);
      }
    });

    const rootNodes: WbsNode[] = [];

    nodes.forEach((node) => {
      const parent = node.parentId
        ? byId.get(node.parentId)
        : undefined;

      if (!parent || parent.id === node.id) {
        rootNodes.push(node);
      } else {
        parent.children.push(node);
      }
    });

    const sortNodes = (items: WbsNode[]) => {
      items.sort(
        (a, b) =>
          a.sequence - b.sequence ||
          a.name.localeCompare(b.name)
      );

      items.forEach((item) => sortNodes(item.children));
    };

    sortNodes(rootNodes);

    // Preserve records that would otherwise be hidden by a parent cycle.
    const visited = new Set<string>();

    const markVisited = (items: WbsNode[]) => {
      items.forEach((node) => {
        if (visited.has(node.id)) return;

        visited.add(node.id);
        markVisited(node.children);
      });
    };

    markVisited(rootNodes);

    nodes.forEach((node) => {
      if (!visited.has(node.id)) {
        rootNodes.push(node);
      }
    });

    sortNodes(rootNodes);

    return rootNodes;
  }, [records]);

  // Filter WBS records by name or description.
  const filteredRoots = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return roots;

    const filterNode = (node: WbsNode): WbsNode | null => {
      const children = node.children
        .map(filterNode)
        .filter((child): child is WbsNode => child !== null);

      const matches =
        node.name.toLowerCase().includes(term) ||
        node.description.toLowerCase().includes(term);

      if (!matches && children.length === 0) {
        return null;
      }

      return { ...node, children };
    };

    return roots
      .map(filterNode)
      .filter((node): node is WbsNode => node !== null);
  }, [roots, search]);

  // Expand parent nodes so the hierarchy is visible.
  useEffect(() => {
    const ids = new Set<string>();

    const collectIds = (nodes: WbsNode[]) => {
      nodes.forEach((node) => {
        if (node.children.length > 0) {
          ids.add(node.id);
        }

        collectIds(node.children);
      });
    };

    collectIds(filteredRoots);
    setExpanded(ids);
  }, [filteredRoots]);

  const toggleSelection = (id: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setAddMessage("");
  };

  const handleAddItems = () => {
    if (selectedIds.size === 0) {
      setAddMessage("Select at least one WBS item first.");
      return;
    }
    // UI-only for now: no API call or backend save.
    setAddMessage(`${selectedIds.size} WBS item${selectedIds.size === 1 ? "" : "s"} selected. API integration can be added later.`);
  };

  const toggleNode = (id: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const totalNodes = records.filter(
    (record) => !isDeleted(record)
  ).length;

  // Render the WBS tree.
  const renderNodes = (
    nodes: WbsNode[],
    depth = 0
  ): React.ReactNode =>
    nodes.map((node) => {
      const hasChildren = node.children.length > 0;
      const isOpen = expanded.has(node.id);

      return (
        <React.Fragment key={`${node.id}-${depth}`}>
          <div
            className="flex min-w-0 items-start gap-2 border-b border-gray-100 px-3 py-2.5 hover:bg-blue-50"
            style={{ paddingLeft: `${12 + depth * 24}px` }}
          >
            <div className="flex w-5 shrink-0 justify-center pt-0.5">
              {hasChildren ? (
                <button
                  type="button"
                  onClick={() => toggleNode(node.id)}
                  aria-label={
                    isOpen ? "Collapse item" : "Expand item"
                  }
                  className="rounded px-1 text-gray-600 hover:bg-gray-200"
                >
                  {isOpen ? "−" : "+"}
                </button>
              ) : (
                <span className="text-gray-300">•</span>
              )}
            </div>

            <label className="flex shrink-0 items-start pt-0.5" aria-label={`Select ${node.name}`}>
              <input
                type="checkbox"
                checked={selectedIds.has(node.id)}
                onChange={() => toggleSelection(node.id)}
                className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </label>

            <div className="min-w-0 flex-1">
              <div className="break-words font-medium text-gray-800">
                {node.name}
              </div>

              {node.description && (
                <div className="mt-1 whitespace-pre-wrap break-words text-xs text-gray-500">
                  {node.description}
                </div>
              )}
            </div>

          </div>

          {hasChildren &&
            isOpen &&
            renderNodes(node.children, depth + 1)}
        </React.Fragment>
      );
    });

  return (
    <div
      className="flex h-screen flex-col overflow-hidden bg-gray-100 p-3 sm:p-5"
      style={{ fontFamily: "'Segoe UI', sans-serif" }}
    >
      {/* Header: Back button, project name, username and Logout */}
      <header className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/baevr-projects")}
            className="shrink-0 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            ← Back
          </button>

          <div className="min-w-0">
            <h1 className="truncate text-xl font-bold text-gray-800 sm:text-2xl">
              {projectName} — WBS
            </h1>

             
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          <span className="font-medium text-gray-700">
            {userName}
          </span>

          <button
            type="button"
            onClick={handleLogout}
            className="rounded-lg bg-red-600 px-4 py-2 font-semibold text-white transition hover:bg-red-700"
          >
            Logout
          </button>
        </div>
      </header>

      {/* WBS list uses the available screen height */}
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-3">
          <div className="min-w-0">
            <h2 className="font-semibold text-gray-800">Work Breakdown Structure</h2>
            <p className="mt-0.5 text-xs text-gray-500">Select one or more items using the checkboxes.</p>
          </div>

          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search WBS items..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 sm:w-64"
          />
        </div>

        <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          WBS Items
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center p-10 text-center text-gray-500">
            Loading WBS records...
          </div>
        ) : error ? (
          <div className="p-6">
            <p className="font-medium text-red-600">{error}</p>

            <p className="mt-2 text-sm text-gray-500">
              Open Developer Tools → Network and check the WBS request
              and its response.
            </p>

            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        ) : filteredRoots.length === 0 ? (
          <div className="flex flex-1 items-center justify-center p-10 text-center text-gray-500">
            {records.length === 0
              ? "No WBS records were returned for this project."
              : "No matching WBS items found."}
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            {renderNodes(filteredRoots)}
          </div>
        )}

        <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-700">
              {selectedIds.size} item{selectedIds.size === 1 ? "" : "s"} selected
            </p>
            {addMessage && (
              <p className="mt-1 text-xs text-gray-500" role="status">{addMessage}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => { setSelectedIds(new Set()); setAddMessage(""); }}
              disabled={selectedIds.size === 0}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Clear Selection
            </button>
            <button
              type="button"
              onClick={handleAddItems}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
            >
              Add Items ({selectedIds.size})
            </button>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default BaevrWbs;
